import React, { FC, useEffect, useState } from 'react';
import { isAfter, isBefore } from 'date-fns';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';

import { AppGroup, Application } from 'reduxState/store/application/types';
import { ICampaign } from 'reduxState/store/campaigns/types';
import CampaignAppSelector from '../CampaignForms/CampaignAppsSelector/CampaignAppsSelector';
import { Clear } from '@mui/icons-material';
import EditCampaignForm from '../CampaignForms/EditCampaignForm/EditCampaignForm';

interface IEditAppGroupCampaign {
  appGroup: AppGroup;
  campaignToEdit: ICampaign;
  setSelectedCampaignToEdit: (campaign: ICampaign | null) => void;
  isModalOpen: boolean;
  handleCloseModal: () => void;
}

const EditAppGroupCampaign: FC<IEditAppGroupCampaign> = ({
  appGroup,
  campaignToEdit,
  setSelectedCampaignToEdit,
  isModalOpen,
  handleCloseModal,
}) => {
  const [selectedApps, setSelectedApps] = useState<Application[]>([]);
  const [hasSelectedAppsError, setHasSelectedAppsError] = useState(false);
  const [isStartDateDisabled, setIsStartDateDisabled] = useState(false);
  const [isEditingDisabled, setIsEditingDisabled] = useState(false);
  const [reset, setReset] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [hasFormChanges, setHasFormChanges] = useState(false);

  const requestClose = () => {
    if (hasFormChanges) setShowCloseConfirm(true);
    else handleCloseModal();
  };
  const confirmClose = () => {
    setShowCloseConfirm(false);
    handleCloseModal();
  };
  const cancelClose = () => setShowCloseConfirm(false);

  useEffect(() => {
    const { startDate, endDate, disabled } = campaignToEdit;

    const currentDate = new Date();
    const campaignStartDate = new Date(startDate);
    const campaignEndDate = new Date(endDate);

    if (disabled || isAfter(currentDate, campaignEndDate)) {
      setIsEditingDisabled(true);
      setIsStartDateDisabled(true);
    } else if (isBefore(currentDate, campaignStartDate)) {
      setIsEditingDisabled(false);
      setIsStartDateDisabled(false);
    } else if (isAfter(currentDate, campaignStartDate) && isBefore(currentDate, campaignEndDate)) {
      setIsEditingDisabled(false);
      setIsStartDateDisabled(true);
    }
  }, [campaignToEdit]);

  const resetSelectedApps = () => {
    setSelectedApps([]);
    setHasSelectedAppsError(false);
    setReset(true);
    // Reset the reset flag after a short delay to allow the effect to run
    setTimeout(() => setReset(false), 100);
  };

  return (
    <>
    <Dialog
      open={isModalOpen}
      classes={{ paper: 'p-3 sm:p-4 pb-4 w-[calc(100vw-16px)] max-w-[1400px] min-w-0 m-2 sm:m-4 h-auto max-h-[100dvh] overflow-y-auto overflow-x-hidden' }}
      onClose={() => requestClose()}
      fullWidth
      maxWidth="lg"
    >
      <div className="absolute top-2 right-2 sm:top-[15px] sm:right-[15px] cursor-pointer z-10 p-1" onClick={requestClose}>
        <Clear />
      </div>
      <div className="flex flex-col w-full min-w-0 max-w-full overflow-x-hidden p-4 sm:p-6 md:p-8 pb-6 bg-white min-h-0 box-border">
        <h3 className="m-0 mb-4 text-muted-dark-purple">
          Edit {appGroup.adminName} Generic Conquesting Campaign (ID: {campaignToEdit.id})
        </h3>
        <div className="mb-4">
          <h4 className="text-muted-dark-purple">Applications</h4>
          <CampaignAppSelector
            appGroup={appGroup}
            setSelectedApps={setSelectedApps}
            hasSelectedAppsError={hasSelectedAppsError}
            setHasSelectedAppsError={setHasSelectedAppsError}
            reset={reset}
            campaign={campaignToEdit}
          />
        </div>
        <EditCampaignForm
          selectedApps={selectedApps}
          setHasSelectedAppsError={setHasSelectedAppsError}
          resetSelectedApps={resetSelectedApps}
          campaignToEdit={campaignToEdit}
          setSelectedCampaignToEdit={setSelectedCampaignToEdit}
          isEditingDisabled={isEditingDisabled}
          isStartDateDisabled={isStartDateDisabled}
          handleCloseModal={handleCloseModal}
          onHasFormChangesChange={setHasFormChanges}
        />
      </div>
    </Dialog>
    <Dialog open={showCloseConfirm} onClose={cancelClose}>
      <DialogTitle>Discard changes?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Your progress will be lost. Are you sure you want to close?
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={cancelClose}>Cancel</Button>
        <Button onClick={confirmClose} color="error" variant="contained">
          Discard
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default EditAppGroupCampaign;
